import React from 'react';
import { FormControl, Select, MenuItem, Box, Typography, SelectChangeEvent } from '@mui/material';
import { useTenant } from './TenantContext';

/**
 * TenantSelector Component
 *
 * A dropdown selector that allows users to switch between different store locations.
 * Follows Material-UI best practices for accessible form controls.
 *
 * Features:
 * - Displays all available stores/tenants
 * - Persists selection via TenantContext
 * - Compact design suitable for header placement
 * - Accessible with proper ARIA labels
 */
export const TenantSelector: React.FC = () => {
  const { currentTenant, tenants, loading, setCurrentTenant } = useTenant();

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedSlug = event.target.value;
    const tenant = tenants.find((t) => t.slug === selectedSlug);

    if (tenant) {
      // setCurrentTenant handles localStorage persistence internally
      setCurrentTenant(tenant);
    }
  };

  // Show loading spinner only when actually loading
  if (loading) {
    return (
      <Box sx={{ minWidth: 200 }}>
        <Typography variant="body2" color="text.secondary">
          Loading stores...
        </Typography>
      </Box>
    );
  }

  // Not loading but no tenants available (auth failed or no permissions)
  if (tenants.length === 0) {
    return (
      <Box sx={{ minWidth: 200 }}>
        <Typography variant="body2" color="text.secondary">
          No stores available
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl
      size="small"
      data-testid="tenant-selector-container"
      sx={{
        minWidth: 200,
        position: 'relative',
        // Keep selector clickable in dense headers and ensure the menu renders above app bars/overlays.
        zIndex: 1400,
        '& .MuiOutlinedInput-root': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Select
        value={currentTenant?.slug ?? ''}
        onChange={handleChange}
        displayEmpty
        data-testid="tenant-selector"
        inputProps={{
          'aria-label': 'Select store location',
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: { zIndex: 1500 },
            },
          },
        }}
        sx={{
          fontSize: '0.875rem',
          '& .MuiSelect-select': {
            py: 1,
            px: 1.5,
          },
        }}
      >
        {/* Show placeholder when no tenant is selected but tenants exist */}
        {!currentTenant && tenants.length > 0 && (
          <MenuItem value="" disabled>
            Select a store
          </MenuItem>
        )}

        {tenants.map((tenant) => (
          <MenuItem
            key={tenant.id}
            value={tenant.slug}
            sx={{
              fontSize: '0.875rem',
            }}
          >
            <Box>
              <Typography variant="body2" component="span">
                {tenant.name}
              </Typography>
              {!tenant.isActive && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 1 }}
                >
                  (Inactive)
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Optional: Compact version for minimal header space
 */
export const TenantSelectorCompact: React.FC = () => {
  const { currentTenant, tenants, loading, switching, setCurrentTenant } = useTenant();

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedSlug = event.target.value;
    const tenant = tenants.find((t) => t.slug === selectedSlug);

    if (tenant) {
      // setCurrentTenant handles localStorage persistence internally
      setCurrentTenant(tenant);
    }
  };

  // Don't render compact selector while loading or if no tenants/selection
  if (loading || tenants.length === 0 || !currentTenant) {
    return null;
  }

  return (
    <FormControl size="small" sx={{ minWidth: 180, position: 'relative', zIndex: 1400 }}>
      <Select
        value={currentTenant?.slug ?? ''}
        onChange={handleChange}
        displayEmpty
        disabled={loading || switching || tenants.length === 0}
        inputProps={{
          'aria-label': 'Select store location',
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: { zIndex: 1500 },
            },
          },
        }}
        renderValue={(selected) => {
          if (loading) return 'Loading...';
          if (!selected) return 'Select store';
          // Show full name in the selected display
          const tenant = tenants.find((t) => t.slug === selected);
          return tenant ? tenant.name : 'Select store';
        }}
        sx={{
          fontSize: '0.8rem',
          '& .MuiSelect-select': {
            py: 0.5,
            px: 1,
          },
        }}
      >
        {/* Placeholder (so the control doesn't disappear when tenant isn't resolved yet) */}
        <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}>
          {loading ? 'Loading stores...' : 'Select store'}
        </MenuItem>
        {tenants.map((tenant) => (
          <MenuItem key={tenant.id} value={tenant.slug} sx={{ fontSize: '0.8rem' }}>
            {tenant.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
